import { Container } from '@acies/core';
import { IconTitleSection, Spinner } from '@acies/ui';
import { ConfiguratorModel } from '../configurator.model';
import DrawingViewer from './drawing-viewer';
import FrontBackToggleButtonBar from './front-back-toggle-button-bar';
import ShowGeometryDialogButton from './show-geometry-dialog-button';

export default class DrawingSection extends IconTitleSection {
	#drawingViewer!: DrawingViewer;
	#spinner!: Spinner;
	#buttons!: Container;
	#showGeometryDialogButton!: ShowGeometryDialogButton;
	#frontBackToggleButtonBar!: FrontBackToggleButtonBar;
	public constructor() {
		super();
		this.width = 'fill';
		this.height = 'fill';
		this.icon = 'transform';
		this.text = 'Tegning';
		// centred, for the spinner: the viewer fills the body whatever its alignment, and the spinner
		// sits in the middle of the room the viewer leaves while it is hidden
		this.body.alignment = 'center';
		// the listener is registered on the model, so it would otherwise run in its scope rather
		// than in this one
		this.waitingChanged = this.waitingChanged.bind(this);
		// Subscribed here rather than on connect, because an Observable tells its listeners about a
		// change and not about the value it already holds. What the model already holds is read on
		// connect, in initialize, once the viewer and the spinner exist.
		ConfiguratorModel.currentDrawing.subscribe(this.waitingChanged);
		ConfiguratorModel.configurationInProgress.subscribe(this.waitingChanged);
		ConfiguratorModel.calculationInProgress.subscribe(this.waitingChanged);
	}

	/**
	 * The viewer the drawing is shown in.
	 *
	 * It follows the model itself, so the section only decides where it sits. Exposed for the tests, which read what it is showing.
	 */
	public get drawingViewer(): DrawingViewer {
		if (!this.#drawingViewer) {
			this.#drawingViewer = new DrawingViewer();
		}
		return this.#drawingViewer;
	}

	/**
	 * Says that a drawing is on its way, in the middle of the room it will take.
	 *
	 * Shown only while a configuration or a calculation is out for the selected position and there is no drawing to show — see {@link DrawingSection.waitingChanged}. A drawing already on screen is left there while another is worked out, since a stale drawing says more than a spinner.
	 */
	public get spinner(): Spinner {
		if (!this.#spinner) {
			this.#spinner = new Spinner();
			this.#spinner.visible = false;
		}
		return this.#spinner;
	}

	public get buttons(): Container {
		if (!this.#buttons) {
			this.#buttons = new Container();
			this.#buttons.gapHorizontal = 16;
			this.#buttons.addComponents([this.showGeometryDialogButton, this.frontBackToggleButtonBar]);
		}
		return this.#buttons;
	}

	public get showGeometryDialogButton(): ShowGeometryDialogButton {
		if (!this.#showGeometryDialogButton) {
			this.#showGeometryDialogButton = new ShowGeometryDialogButton();
		}
		return this.#showGeometryDialogButton;
	}

	public get frontBackToggleButtonBar(): FrontBackToggleButtonBar {
		if (!this.#frontBackToggleButtonBar) {
			this.#frontBackToggleButtonBar = new FrontBackToggleButtonBar();
		}
		return this.#frontBackToggleButtonBar;
	}

	protected override initialize(): void {
		super.initialize();
		this.header.addComponents([this.buttons]);
		this.body.addComponents([this.drawingViewer, this.spinner]);
		this.waitingChanged();
	}

	/**
	 * Unsubscribes, because the model outlives the section.
	 *
	 * `ConfiguratorModel` is a module constant, so a listener it still holds is a section that has been removed and cannot be collected.
	 */
	protected override disconnected(): void {
		super.disconnected();
		ConfiguratorModel.currentDrawing.unsubscribe(this.waitingChanged);
		ConfiguratorModel.configurationInProgress.unsubscribe(this.waitingChanged);
		ConfiguratorModel.calculationInProgress.unsubscribe(this.waitingChanged);
	}

	/**
	 * Gives the body to the spinner while a drawing is on its way and none is on screen, and back to the viewer otherwise.
	 *
	 * Read off the model as a whole rather than off the value that changed, because it is three values together that say whether to wait: a call out for the selected position — see `configurationInProgress` and `calculationInProgress` — and no drawing to look at meanwhile. The viewer is taken out of the layout rather
	 * than left empty beside the spinner, because it fills whatever it is given: left in, it would keep the room and push the spinner aside.
	 */
	private waitingChanged(): void {
		const waiting = (ConfiguratorModel.configurationInProgress.value || ConfiguratorModel.calculationInProgress.value) && ConfiguratorModel.currentDrawing.value === null;
		this.spinner.visible = waiting;
		this.spinner.spin = waiting;
		this.drawingViewer.visible = !waiting;
	}
}
customElements.define('ac-drawing-section', DrawingSection);
